import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button';
import { toast } from 'sonner';
import { scanSkillBeforeInstall } from '../endpoints/taskingbot/scan-skill_POST.schema';
import styles from './PackageManagerModal.module.css';

// SkillsMP API key is stored server-side via SKILLS_MP wrangler secret
// Frontend proxies through /_api/skills/search to keep the key secure

interface SkillResult {
  name: string;
  fullName: string;
  description: string;
  stars: number;
  url: string;
  skillMdUrl?: string;
  source?: 'github' | 'skillsmp' | 'clawdhub' | 'github-openclaw' | 'openclaw' | 'local';
}

function buildSkillKey(skill: { fullName: string; name: string }): string {
  return `${skill.fullName}/${skill.name}`;
}

function rankSkillResults(
  skills: SkillResult[],
  query: string,
  installedKeys: Set<string>,
): SkillResult[] {
  const q = query.trim().toLowerCase();
  if (!q) return skills;
  const tokens = q.split(/\s+/).filter(Boolean);

  const scoreSkill = (skill: SkillResult): number => {
    const name = skill.name.toLowerCase();
    const full = skill.fullName.toLowerCase();
    const desc = (skill.description || '').toLowerCase();
    let score = 0;

    if (installedKeys.has(buildSkillKey(skill))) score += 10000;

    // Strong exact/near-exact boosts
    if (name === q) score += 2500;
    if (full === q) score += 2200;
    if (name.startsWith(q)) score += 1200;
    if (full.startsWith(q)) score += 900;
    if (name.includes(q)) score += 700;
    if (full.includes(q)) score += 500;
    if (desc.includes(q)) score += 250;

    for (const token of tokens) {
      if (name === token) score += 400;
      if (name.startsWith(token)) score += 220;
      if (name.includes(token)) score += 140;
      if (full.includes(token)) score += 90;
      if (desc.includes(token)) score += 35;
    }

    // Keep stars as a soft tie-breaker, not a dominance factor.
    score += Math.min(skill.stars || 0, 5000) / 250;
    return score;
  };

  return [...skills].sort((a, b) => {
    const aInstalled = installedKeys.has(buildSkillKey(a));
    const bInstalled = installedKeys.has(buildSkillKey(b));
    if (aInstalled !== bInstalled) return aInstalled ? -1 : 1;

    const scoreDiff = scoreSkill(b) - scoreSkill(a);
    if (scoreDiff !== 0) return scoreDiff;

    const starDiff = (b.stars || 0) - (a.stars || 0);
    if (starDiff !== 0) return starDiff;

    return a.name.localeCompare(b.name);
  });
}

export interface InstalledSkill {
  name: string;
  fullName: string;
  description: string;
  url: string;
  skillMdUrl?: string;
  active: boolean;
  installedAt: string;
  content?: string; // Full SKILL.md content fetched from the repo
}

const STORAGE_KEY = 'tasking-installed-skills';

/** Save installed skills to localStorage */
export function saveInstalledSkills(skills: InstalledSkill[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(skills));
  } catch (e) {
    console.error('Failed to save installed skills:', e);
  }
}

const SESSION_SKILLS_PREFIX = 'tasking-session-skills-';

/** Load the list of skill names active for a specific session.
 *  Returns null if no per-session override exists (first visit). */
export function loadSessionActiveSkillNames(sessionId: string): string[] | null {
  try {
    const stored = localStorage.getItem(SESSION_SKILLS_PREFIX + sessionId);
    if (stored) return JSON.parse(stored) as string[];
  } catch {
    /* ignore */
  }
  return null;
}

/** Save the list of skill names active for a specific session. */
export function saveSessionActiveSkillNames(sessionId: string, names: string[]) {
  try {
    localStorage.setItem(SESSION_SKILLS_PREFIX + sessionId, JSON.stringify(names));
  } catch {
    /* best-effort */
  }
}

/** Get the active InstalledSkill[] for a specific session.
 *  On first visit (no per-session key), inherits from globally active skills. */
export function loadSessionActiveSkills(sessionId: string): InstalledSkill[] {
  const allInstalled = loadInstalledSkills();
  const perSession = loadSessionActiveSkillNames(sessionId);
  if (perSession !== null) {
    // Filter installed skills to only those active for this session
    const nameSet = new Set(perSession);
    return allInstalled.filter((s) => nameSet.has(s.name));
  }
  // First visit — start with no skills active; user must explicitly enable them
  saveSessionActiveSkillNames(sessionId, []);
  return [];
}

interface SkillSearchModalProps {
  open: boolean;
  onClose: () => void;
  onInstallSkill: (command: string) => void;
  initialQuery?: string;
  onActiveSkillsChange?: (skills: InstalledSkill[]) => void;
  searchEndpoint?: string; // Custom search endpoint (e.g. '/_api/taskingbot/skills-search' for TaskingBot)
}

const KNOWN_SOURCES: SkillResult[] = [
  {
    name: 'frontend-design',
    fullName: 'anthropics/skills',
    description:
      'Create distinctive, production-grade frontend interfaces with high design quality',
    stars: 0,
    url: 'https://github.com/anthropics/skills',
  },
  {
    name: 'wrangler',
    fullName: 'cloudflare/skills',
    description:
      'Cloudflare Wrangler — Workers, Pages, D1, R2, KV, and Cloudflare deployment workflows',
    stars: 0,
    url: 'https://github.com/cloudflare/skills',
  },
  {
    name: 'skill-creator',
    fullName: 'anthropics/skills',
    description: 'Create custom agent skills — define new capabilities and workflows for AI agents',
    stars: 0,
    url: 'https://github.com/anthropics/skills',
  },
  {
    name: 'find-skills',
    fullName: 'vercel-labs/skills',
    description: 'CLI for the open agent skills ecosystem — search, install, and manage skills',
    stars: 0,
    url: 'https://github.com/vercel-labs/skills',
  },
  {
    name: 'Godot-Engine-3D-Game-Developer',
    fullName: 'tippyentertainment/skills',
    description:
      '3D game development with Godot Engine — task management, builds, testing, assets, and CI workflows',
    stars: 0,
    url: 'https://github.com/tippyentertainment/skills',
  },
  {
    name: 'video-avatars-and-virtual-backgrounds',
    fullName: 'tippyentertainment/skills',
    description:
      'Real-time video effects — selfie segmentation, virtual backgrounds, face mesh tracking, and avatar replacement using WebRTC & MediaPipe',
    stars: 0,
    url: 'https://github.com/tippyentertainment/skills',
  },
  {
    name: 'rust-wasm',
    fullName: 'pluginagentmarketplace/custom-plugin-rust',
    description:
      'Rust WebAssembly — wasm-pack, wasm-bindgen, browser integration, and WASM optimization',
    stars: 0,
    url: 'https://github.com/pluginagentmarketplace/custom-plugin-rust',
  },
  {
    name: 'worldclass-tailwind-v4-visual-design',
    fullName: 'tippyentertainment/skills',
    description:
      'World-class visual/UI design with Tailwind v4, Gemini image generation, cinematic layouts, and award-winning art direction',
    stars: 0,
    url: 'https://github.com/tippyentertainment/skills',
  },
];

const TASKINGBOT_CURATED_SOURCES = KNOWN_SOURCES.filter(
  (skill) =>
    skill.fullName === 'tippyentertainment/skills' || skill.fullName === 'tasking-tech/skills',
);

// Built-in skill content for autonomous multi-agent coordination.
// This is embedded directly so it doesn't need a GitHub fetch.
const AUTONOMOUS_MULTI_AI_AGENTS_SKILL = `---
name: autonomous-multi-ai-agents
description: >
  Coordinate multiple AI agents that can chat, use voice, and edit code/files/folders
  simultaneously while avoiding destructive collisions. Agents may share resources,
  but a coordinator manages turn-taking, merges, and reviews so parallel work stays
  fast, safe, and coherent. Agents communicate via AI chat, team chat, and collab
  chat channels to coordinate plans and changes.
---
# Provided by TippyEntertainment
# https://github.com/tippyentertainment/skills.git

goals:
  - Allow multiple agents to work concurrently on the same project or session.
  - Route user input (text/voice) to the right agent or group of agents.
  - Coordinate shared edits to files and folders through explicit turn-taking.
  - Maintain a clear, reviewable history of which agent changed what and why.
  - Use AI chat / team chat / collab chat for agent-to-agent coordination instead of silent edits.

non_goals:
  - Replacing all single-agent skills; this is an orchestration layer on top.
  - Building new domain skills (e.g., new coding, design, or research capabilities).
  - Directly managing infrastructure beyond what's exposed by tools.
  - Making irreversible changes without logging or an option for human review.

roles:
  - coordinator:
      description: >
        Supervises all agents, assigns tasks, manages shared-resource schedules,
        and resolves conflicts. Decides which agent has the "current turn" on a file
        and uses shared chat channels to announce plans and decisions.
  - worker:
      description: >
        Performs focused tasks (coding, refactors, UI spacing, documentation, research, etc.)
        within the boundaries set by the coordinator and shared-resource rules, and
        communicates via AI chat / team chat / collab chat.
  - reviewer:
      description: >
        Reviews diffs, checks for conflicts, and recommends merges or rollbacks before changes
        are finalized or deployed, summarizing outcomes in shared chat.

triggers:
  - The user asks for several things at once across the same codebase.
  - Multiple agents are enabled on the same repo or folder tree.
  - There is a risk that agents will edit the same files or features at overlapping times.
  - Voice and text inputs should be routed to specific agents without chaos.

resources:
  - source_code_repos
  - project_files_and_folders
  - conversation_threads (user <-> agents, agent <-> agent)
  - voice_input_output_streams
  - logs / change history

communication_channels:
  - ai_chat:
      description: >
        Per-agent or per-task chat where an agent can reason, ask clarifying questions,
        and receive instructions from the user or coordinator.
  - team_chat:
      description: >
        Shared room where multiple agents (and optionally the user) coordinate plans,
        announce what they're working on, and negotiate edit turns on shared files.
  - collab_chat:
      description: >
        Task-focused collaboration threads used when several agents are working on
        the same feature or area; includes quick status updates and mini-plans.

coordination_model:
  - shared_resources:
      - Files and folders are shared by default; multiple agents may intend to work on them.
      - The coordinator controls "edit turns" rather than hard exclusive locks.
      - Only one agent at a time should be actively writing to a given file, but others may read,
        analyze, or stage draft changes.
  - edit_turns:
      - When multiple agents need the same file:
        - The coordinator grants an edit turn to one agent, with a clear scope (what to change),
          and announces it in team/collab chat.
        - Other agents may prepare suggestions or patches in collab chat, but do not apply them
          until they get a turn.
      - Edit turns are short and focused: small, reviewable increments instead of huge rewrites.
  - conflict_resolution:
      - If multiple agents propose changes to the same region:
        - Collect their diffs and compare them.
        - Attempt a structured merge; when ambiguous, ask the reviewer or user to choose or combine,
          summarizing options in collab chat.
      - Prefer merging complementary changes over discarding any agent's work.

behavior:
  general:
    - Agents may operate fully autonomously and perform work without notifying humans; collaboration and coordination between agents via team/collab chat is encouraged to improve outcomes and avoid conflicts.
    - Keep an up-to-date mental map of:
      - Active tasks and their owner agents.
      - Which files are currently being edited, and by whom.
      - Which agents have pending suggestions for shared files.
    - Use team/collab chat to:
      - Announce who is working where.
      - Share mini-plans before touching shared resources.
      - Call out when a turn starts or ends on a critical file.
    - Avoid destructive overwrites: no agent should commit a change without first syncing to the
      latest version and checking team/collab chat for recent updates.

  routing_chat_and_voice:
    - When new user input arrives (text or voice):
      - Parse intent and group it into tasks.
      - Decide whether a single agent or multiple agents should handle it.
      - Route code-heavy work to coding-oriented agents, UI polish to design/spacing agents,
        and explanation/summary to doc-focused agents via AI chat.
    - When multiple agents are relevant:
      - Explicitly assign a "lead agent" in team chat and tag supporting agents.
      - Keep the user informed in the main chat about who is doing what.

  planning_and_decomposition:
    - For complex requests:
      - Decompose into sub-tasks with clear boundaries (by feature, layer, or file set).
      - Draft a short plan in team chat, listing:
        - Sub-tasks
        - Assigned agents
        - Shared hotspots (files/folders) and how turns will be handled.
      - Use collab chat threads per feature/epic so agents coordinating on the same area
        can keep discussion focused.
    - Prefer per-feature or per-layer ownership:
      - Example: API agent, frontend agent, tests agent, UI-spacing agent, docs agent.
      - Shared files (e.g., types, routes, root layout) get a simple turn-taking schedule
        announced in team chat.

  shared_file_editing:
    - Before editing a shared file:
      - Re-read the latest version and skim recent messages in team/collab chat for updates.
      - Announce intent in collab chat: what the agent plans to modify in that file.
      - Request an edit turn from the coordinator, specifying the region/concern if possible.
    - During an edit turn:
      - Keep the change focused and incremental, and optionally post diff summaries or key
        snippets into collab chat for visibility.
    - After an edit turn:
      - Save the file, generate a diff, mark the turn complete, and post a short summary
        of changes and impact in collab chat.
      - Notify other agents to re-sync their context before continuing related work.

  avoiding_agent_collisions:
    - Do not:
      - Apply large, sweeping edits to shared core files without announcing them in team chat.
      - Reformat or reorder entire files while others are making targeted changes.
    - Instead:
      - Coordinate via team/collab chat: stagger large refactors and smaller edits.
      - Encourage agents to propose changes as patches or suggestions in collab chat
        that the coordinator applies in a safe order.
    - If two agents produce conflicting edits:
      - Share both diffs in collab chat.
      - Attempt a merge and explain the merge decision.
      - If unclear, pause edits on that file and ask the reviewer or user.

  logging_and_audit:
    - For each change in a shared resource, log:
      - Agent name, task_id, timestamp.
      - Files touched and a brief description of intent and outcome.
    - Reflect important log entries into team or collab chat (e.g., "Task-42: frontend-agent
      updated Header.tsx padding and spacing for the nav bar").
    - Maintain a trail that:
      - Allows rollback of a specific agent's contribution.
      - Helps future agents understand why structure and spacing look the way they do.

  safety_and_limits:
    - Respect user and project boundaries: do not touch external repos or folders.
    - Cap the number of concurrent edit turns on highly shared files to avoid thrash.
    - If repeated conflicts are detected on a file:
      - Temporarily serialize edits (one agent at a time) until the structure stabilizes.
      - Optionally nominate a "file steward" agent and note that in team chat.

examples_of_tasks:
  - "Have multiple agents code a feature, wire UI, and adjust spacing, all touching shared layout files while coordinating via team chat."
  - "Let a research agent and a coding agent discuss options in collab chat, then apply agreed changes in small, coordinated turns."
  - "Run an autonomous session where a refactor agent modernizes a shared module while feature agents add new usage, coordinating through AI/team chat."
  - "Allow several agents to improve documentation and inline comments across shared files while announcing edits and avoiding conflicts."
`;

// Built-in skill for host+worker multi-agent orchestration with endpoint switching.
const HOSTED_MULTI_AGENT_ORCHESTRATOR_SKILL = `---
name: hosted-multi-agent-orchestrator
description: >
  Coordinate multiple AI agents (host + workers) that can listen, speak, and code
  in parallel using /api/chat and /api/generate. The host agent stays present at all
  times, can jump in to steer or summarize, and makes endpoint choices per subtask.
  The experience is "podcast-like" (guided multi-voice collaboration).
---

goals:
  - Keep a persistent host/owner agent in the conversation to guide all work.
  - Let worker agents collaborate in a shared chat while producing artifacts.
  - Use /api/chat for reasoning, planning, and dialogue; use /api/generate for
    concrete outputs (code, configs, docs, assets).
  - Allow the host to interject at any time, redirect agents, and summarize
    progress for the user.
  - Avoid agent collisions by coordinating tasks and shared resources.
  - Support listen/speak/code: agents can use voice input (STT) and voice output
    (TTS) alongside text chat and code generation.

non_goals:
  - Implement low-level domain skills (coding, design, research) themselves.
  - Produce audio or an actual podcast UI.
  - Replace existing single-agent behaviors; this skill orchestrates them.
  - Directly manage deployment or infrastructure beyond calling existing APIs.

roles:
  - host:
      description: >
        Persistent owner/guide of the session. The lead (owner) who ALWAYS has final
        say. Listens to all agents and the user, asks clarifying questions, decides
        priorities, and chooses when to use /api/chat vs /api/generate. Can interrupt
        or redirect any agent at any time. Stays present throughout the entire session
        and periodically summarizes progress.
  - worker:
      description: >
        Focused agents (coding, UI, docs, research, etc.) that perform tasks
        delegated by the host. They reason via /api/chat, use /api/generate to
        emit artifacts, and post updates to shared chat.
  - reviewer:
      description: >
        Optional role to review artifacts produced by workers, compare options,
        and recommend merges or changes. Summarizes tradeoffs back to host/user.

endpoints:
  - /api/chat:
      usage: >
        Use for back-and-forth reasoning, clarifications, planning, and any
        work that benefits from conversational context or memory. Preferred when
        requirements are unclear, multiple agents need to coordinate, or when
        explaining decisions to the user.
  - /api/generate:
      usage: >
        Use for single-shot or short-sequence artifact creation when the inputs
        are already clear (files, config, CSS, prompts, code, tests, etc.).
        Preferred when specs are clear and you want concrete output.

behavior:
  general:
    - Maintain a shared conversation stream (team/collab chat) where host and
      all workers can see each other's messages.
    - Keep the user informed about which agents are active and what they are
      doing, using brief, human-readable messages from the host.
    - Prefer small, incremental changes over large, opaque edits so review and
      rollback remain easy.
    - All agents can listen (voice STT), speak (voice TTS), and code (generate
      artifacts) — these are simultaneous capabilities, not sequential modes.

  host_behavior:
    - Always stay "present" in the conversation:
      - Greet the user and briefly restate goals.
      - Periodically summarize what has been done and what's next.
      - Step in when agents disagree or appear stuck.
    - The host/lead/owner ALWAYS has final say on all decisions.
    - Decide endpoint usage per subtask:
      - Use /api/chat when requirements are unclear, agents need to coordinate,
        or explaining decisions to the user.
      - Use /api/generate when specs are clear and you want concrete output
        (code, docs, configs) or want to transform existing artifacts.
    - Task assignment:
      - Break work into sub-tasks and assign each to a worker agent.
      - Announce assignments and goals in shared chat.
      - Ensure each sub-task has clear inputs, outputs, and success criteria.
    - Intervention:
      - Interrupt any worker at any time to clarify scope or change direction.
      - Ask workers to pause if they conflict with one another.
      - If the user speaks (voice or text), prioritize responding to them,
        adjusting plans as needed.
    - Live chat: The host can chat live with the user at any time, responding
      to voice or text input immediately while workers continue in background.

  worker_behavior:
    - Use /api/chat to reason about complex tasks, ask the host for clarification,
      or coordinate with other workers and explain proposed changes.
    - Use /api/generate to produce artifacts (files, diffs, configs, CSS, docs,
      tests) once requirements are known, or to refactor/transform existing content.
    - Communication:
      - Announce intent before editing shared resources.
      - Post short summaries of what was changed and why.
      - Flag uncertainties or risks and request host guidance instead of guessing.

  reviewer_behavior:
    - Compare multiple worker outputs when the host asks for options.
    - Check artifacts for consistency, bugs, security/UX issues, and style.
    - Provide concise feedback to the host and suggest which version to adopt
      with any needed edits. Request follow-up work from workers if needed.

  coordination_and_collision_avoidance:
    - Use the existing autonomous-multi-ai-agents skill for resource management;
      this skill focuses on conversational orchestration and endpoint choice.
    - Host should avoid assigning overlapping edits to different workers on the
      same file/section at the same time.
    - Workers should respect host decisions about task ownership and sequence,
      and defer to the host when conflicts arise.

  voice_capabilities:
    - All agents can listen via Web Speech API (STT) and speak via Inworld TTS.
    - Echo suppression: mic pauses during TTS playback to prevent feedback.
    - Conversation mode: auto-listen, auto-send to AI, auto-speak response.
    - Dictation mode: continuous transcription for editing before sending.
    - Command mode: voice-activated slash commands.
    - Each agent persona is assigned a unique voice from the Inworld voice pool
      for distinguishable multi-voice conversations.

  explainability:
    - Host periodically summarizes active agents, their tasks, recent decisions,
      next steps, and open questions for the user.
    - Workers briefly explain non-trivial changes, linking to user goals or
      prior host instructions.

examples_of_tasks:
  - "Host + workers design and build a feature: host coordinates, workers handle
     backend, frontend, UI polish, and docs using /api/chat and /api/generate."
  - "Host orchestrates a refactor: chat through the plan, then workers generate
     new modules, tests, and migration docs."
  - "Run a podcast-like multi-agent discussion about architecture options, then
     workers generate diagrams, code scaffolds, and decision records."
  - "Host supervises agents improving UI while another updates APIs, all talking
     live and coordinating in shared chat with voice and text."
`;

// Built-in skill for world-class visual design with Tailwind v4 + Gemini image models.
const WORLDCLASS_TAILWIND_V4_VISUAL_DESIGN_SKILL = `---
target: https://tasking.tech
name: worldclass-tailwind-v4-visual-design
description: >
  A top-tier product/UI designer skill that uses Tailwind v4 plus Google
  Gemini Nano Banana image models to craft visually stunning, "award-winning"
  marketing sites and apps with strong art direction, motion, and systems
  thinking.
---
# Provided by TippyEntertainment
# https://github.com/tippyentertainment/skills.git

goals:
  - Define a clear visual direction (mood, tone, personality) for each project.
  - Translate that direction into Tailwind v4 tokens, themes, and components.
  - Auto-generate on-brand hero images, hero banners, and illustrations using
    Gemini (Nano Banana, Nano Banana Pro, and Nano Banana Ultra Edit) where helpful.
  - Produce layouts that feel cinematic, premium, and highly polished.
  - Maintain a cohesive visual system across pages, states, and breakpoints.
  - Balance aesthetics with accessibility, clarity, and performance.

non_goals:
  - Implement backend logic or data modeling.
  - Handle complex business logic or state management.
  - Replace a full design system; it focuses on the visual/UI layer.
  - Ship generic "template-y" UIs without strong art direction.

role:
  visual_director_designer:
    description: >
      Acts as a world-class product/visual designer who understands modern
      web aesthetics, motion, and UX best practices, and expresses them through
      Tailwind v4 plus Gemini image generation. Owns mood, typography, color,
      spacing, layout patterns, and visual assets (hero art, banners, fillers).

image_generation:
  providers:
    - google_gemini:
        models:
          - nano_banana:
              usage: >
                Fast concept art, mood sketches, simple section illustrations,
                background textures, and low-stakes exploratory hero concepts.
          - nano_banana_pro:
              usage: >
                Final hero images and banners that need crisp detail, good
                lighting, and legible embedded text (e.g. headlines, UI labels).
          - nano_banana_ultra_edit:
              usage: >
                Editing or on-brand remixes of existing assets, like
                screenshot cleanup, background upgrades, recoloring, or
                adjusting lighting/composition to match the site's art direction.
  strategy:
    - prompt_style:
        - Always include brand traits, product type, target audience, and
          desired mood (e.g. "premium, cinematic, atmospheric, subtle noise").
        - Specify camera, composition, and medium when relevant
          (e.g. "wide-angle, 3/4 view, soft studio lighting, volumetric fog").
        - For text-in-image, explicitly define the exact headline and ask for
          clean, legible typography with plenty of padding.
    - quality_and_sizes:
        - Default to high-resolution outputs (2K or 4K where supported) for
          hero art so they can be safely cropped and used across breakpoints.
        - Generate multiple aspect ratios per hero (e.g. 16:9, 3:2, 1:1, 9:16)
          to cover desktop, tablet, and mobile hero crops.
    - variation_and_iteration:
        - Generate 3-6 variations for key hero concepts, then select and
          refine 1-2 with Nano Banana Ultra Edit.
        - Use editing passes to adjust color grading, focus, and background
          detail without changing the core subject.
    - safety_and_brand_consistency:
        - Respect Google's content policies and watermarks when generating
          marketing assets.
        - Prefer semi-abstract, product-centered imagery over overly literal
          depictions that might age poorly.

tailwind_v4_strategy:
  - tokens_and_theme:
      - Define a robust @theme section first: colors, radii, spacing,
        typography, shadows, and transitions.
      - Use OKLCH, P3, and semantic naming (bg-surface, text-soft, etc.)
        instead of ad-hoc hex values.
      - Align color palettes and contrast with the generated imagery (sample
        dominant and accent colors from hero art as tokens).
  - layout_primitives:
      - Build a small set of layout primitives: Section, Container,
        Stack, Cluster, Grid, Split, each with Tailwind class recipes.
      - Ensure these primitives are responsive via container queries and
        modern breakpoints, not just simple stacking.
  - component_patterns:
      - Create reusable patterns (Hero, FeatureGrid, StorySection, Metrics,
        Testimonial, Pricing, Gallery, CTA) with Tailwind class variants.
      - Design for dark/light, noisy/clean, and compact/roomy variants so
        the same pattern can support multiple brands.

workflow:
  - discovery_and_direction:
      - Ask for brand traits, target audience, product type, and vibes
        using concrete references (e.g., "Linear + Apple", "Arc Browser").
      - Propose 2-3 high-level art directions with short copy, layout
        sketches, and initial hero image prompts before committing.
  - system_first:
      - Lock in tokens and primitives (colors, spacing, type ramp, sections)
        plus an initial hero image direction before detailed screens.
      - Document these in a short design-system overview file the devs can
        reference.
  - hero_and_visuals:
      - For each page, define at least:
        - One primary hero image or banner.
        - Supporting illustrations or abstract backgrounds for key sections.
      - Use Nano Banana Pro for the main hero and banners, and Nano Banana /
        Ultra Edit for backgrounds, textures, and contextual imagery.
  - page_composition:
      - Compose pages from the primitives:
        - Hero (narrative opening, bold statement, on-brand hero art).
        - Proof (logos, metrics, social proof).
        - Deep feature stories with product imagery or Gemini-generated scenes.
        - Flows or use cases with diagrammatic visuals.
        - Conversion CTAs and FAQs.
  - refinement:
      - Iterate on micro-details: hover/focus states, easing curves,
        subtle text and card shadows, iconography consistency.
      - Refine hero and key visuals via Nano Banana Ultra Edit to better
        match final typography, spacing, and color system.

output_formats:
  - Tailwind-ready JSX/TSX components with class names fully wired.
  - Tailwind-v4 CSS theme snippets (@theme, @utility examples).
  - Design notes: rationale for key choices (colors, type, imagery).
  - Image specs + prompts:
      - Exact prompts used for each Gemini hero/banner/visual.
      - Recommended cropping, focal points, and usage per breakpoint.

examples_of_tasks:
  - "Design a homepage that feels like an Awwwards winner, including hero
     layout, Tailwind v4 theme, and a set of Gemini-generated hero images."
  - "Create a visual system for an AI product: Tailwind tokens, component
     patterns, and Nano Banana Pro prompts for all main section banners."
  - "Given an existing Tailwind site, redesign the art direction and generate
     new Gemini hero and section visuals to match a more premium aesthetic."
  - "Design a multi-page marketing site where all pages share a cohesive
     Tailwind theme and a library of Gemini-generated images."
`;

const UI_UX_DESIGNER_SKILL = `---
target: https://tasking.tech
name: ui-ux-designer
description: >
  End-to-end UI/UX design skill that takes a feature from vague idea to production-ready UI by moving sequentially through research, requirements, information architecture, wireframing, and visual design without skipping steps.
---

# Provided by TippyEntertainment
# https://github.com/tippyentertainment/skills.git

# UI/UX Designer Skill

This skill requires following a strict sequence of phases:

1. Research and Discovery
2. Define Requirements
3. Information Architecture (IA)
4. Wireframing
5. UI Design

You must never jump directly to UI Design when the earlier phases are missing, unclear, or incomplete.
Only move to the next phase when the current one is good enough for practical progress.

## When to Use This Skill

Use this skill when:
- The user asks for a new feature, screen, flow, or product experience.
- Requirements are fuzzy, underspecified, or conflict with each other.
- Existing UX is confusing, inconsistent, or needs a redesign.
- Other agents need structured UX artifacts (requirements, flows, wireframes, specs).

Do not use this skill for:
- Small visual tweaks to an already well-specified component.
- Pure copywriting or content-only tasks.
- Pixel-perfect Figma implementation details tied to a specific team design system.

## Overall Workflow

Always work in order. At each step:
1. State the current phase.
2. Summarize the inputs you are using.
3. Produce concrete outputs/deliverables.
4. List assumptions and open questions.

Phases:
1) Research and Discovery
2) Define Requirements
3) Information Architecture (IA)
4) Wireframing
5) UI Design

## Phase 1 - Research and Discovery

Goal: Understand the problem, users, and context well enough to make informed tradeoffs.

Tasks:
- Clarify business goals and success metrics.
- Identify primary users/personas and their goals.
- Understand current workflow/status quo.
- Capture constraints: platform, technical limits, compliance, brand.

Outputs:
- Problem Statement (2-4 sentences)
- Primary Users (1-3 user types)
- Goals / Jobs-to-be-Done
- Key Constraints / Assumptions
- Open Questions

## Phase 2 - Define Requirements

Goal: Translate research into explicit, testable requirements.

Tasks:
- Derive user stories.
- Cover happy path and edge cases.
- Separate must-have vs nice-to-have.
- Include UX non-functional requirements (accessibility, responsiveness, performance).

Outputs:
- User Stories: As a <user>, I want <action> so that <outcome>.
- Acceptance Criteria for critical stories.
- Constraints.
- Risks / Tradeoffs.

## Phase 3 - Information Architecture (IA)

Goal: Decide how information/screens are organized and how users move between them.

Tasks:
- Define entities and sections.
- Decide navigation structure.
- Map primary flows.
- Consider future extensibility where reasonable.

Outputs:
- IA Outline / Sitemap.
- Key Entities.
- Primary Flows (step-by-step).

## Phase 4 - Wireframing

Goal: Low-fidelity layout and hierarchy without visual polish.

Principles:
- Focus on layout, grouping, hierarchy, and states.
- Prefer simple textual/ASCII descriptions.
- Capture default, empty, loading, and error states.

Outputs per screen:
- Screen Name and Purpose.
- Layout Description (3-8 bullets).
- States.
- Navigation Hooks.

## Phase 5 - UI Design

Goal: Turn wireframes into coherent, build-ready UI specs.

Tasks:
- Apply spacing, typography, radius, and shadow tokens.
- Ensure color contrast/accessibility.
- Map wireframes to concrete components.
- Define hover/focus/disabled/validation/error states.

Outputs:
- Visual Style Summary.
- Component Mapping.
- Screen Specs (type levels, color tokens, spacing rhythm, radius/shadow, patterns).

## Collaboration Rules

- If partial work exists, validate briefly then continue from next logical phase.
- If new info invalidates earlier work, revise upstream artifacts first, then propagate.
- Keep outputs short, structured, implementation-friendly.
- Prefer clarity over cleverness; state assumptions explicitly.
`;

// Built-in skill for deep web research with multi-query decomposition, source ranking, and citations.
const WEBSEARCH_DEEP_SKILL = `---
name: websearch-deep
description: "Deep web research workflow: problem decomposition, multi-query web search, evidence synthesis with source ranking, citations, and iterative refinement. Use for architecture decisions, multi-domain synthesis, technology selection, strategy, and comparisons."
---

# Deep Web Research Skill

## Purpose

Provide a reusable **deep web research** workflow for complex questions that need multi-source evidence and synthesis, using realtime web search as the primary data source.
This skill orchestrates: decomposition → multi-query web search → evidence ranking → synthesis → structured, cited output.

## When to Use

Use this skill when the question:

- Requires **external, current information** (docs, blogs, forums, papers).
- Benefits from **multiple independent sources** and reconciliation.
- Involves architecture, integration, "best", "compare", "strategy", "migrate", or "trade-offs".

Typical use cases: technology/architecture comparisons, integration design, market/vendor research, deep dives into algorithms or best practices.

---

## Phase 1: Problem Decomposition

Break the main question into **3–5 sub-questions** that together fully cover the user's query.

Sub-questions should be specific, collectively complete, mostly independent, and actionable.

Example — "Best architecture for integrating Salesforce with SQL Server in 2025?":
1. What are Salesforce's current integration capabilities and APIs?
2. What are SQL Server's recommended integration patterns?
3. Which middleware / iPaaS platforms are commonly used?
4. What security and compliance constraints matter?
5. What scalability and performance factors influence the choice?

**Scope rule**: this skill uses **only external web sources** (no repo/file introspection).

---

## Phase 2: Multi-Query Web Search (Realtime)

### Tool Contract

Use the available \`search_web\` tool (or equivalent):
- Input: array of 1–3 short query strings per call.
- Output: array of results with \`{ id, url, title, snippet?, published_at?, source_type? }\`.

Call \`search_web\` multiple times per run, respecting platform limits.

### Query Generation

For each sub-question, generate **3–5 query variants** covering:
- Broad/general angle.
- Specific/technical angle.
- Best practices / patterns.
- Comparisons / alternatives (if relevant).
- Recent updates (e.g., \`after:2024\` where supported).

Prioritize official docs using \`site:\` filters when supported (e.g., \`site:salesforce.com\`, \`site:microsoft.com\`).

### Batching and Parallelism

- Collect all query strings for all sub-questions (≈15–25 total).
- Chunk into batches of **up to 3 queries** per \`search_web\` call.
- Fire batches **in parallel** where your agent framework supports concurrent tool use.
- Each batch result contains search hits for up to 3 queries.

---

## Phase 3: Evidence Aggregation and Ranking

Flatten all results, dedupe by URL, then score each source on:

- **Credibility**: Official/vendor docs → high; reputable blogs/engineering sites → medium; forums/QA → lower.
- **Freshness**: Prefer sources ≤12–18 months old for fast-moving tech; use \`published_at\` when available.
- **Relevance**: How closely does the title/snippet match the sub-question?

Keep top 3–6 sources per sub-question to limit noise.

When sources contradict: check dates and doc versions; prefer official/more recent; present both views with context rather than hiding differences.

---

## Phase 4: Citation Model

Every statement derived from web data must carry **one or more citation IDs** mapping back to search hits.

Pattern:
- Maintain a \`sources\` registry: \`web:1\`, \`web:2\`, … → \`{ url, title }\`.
- Append \`[web:NN]\` after each cited sentence or paragraph.
- Expose the mapping so the UI can render citations as hoverable/clickable links.

Example answer fragment:
> Salesforce exposes REST, Bulk, and Streaming APIs for data integration [web:1]. SQL Server offers ETL, CDC, and linked server mechanisms [web:2].

---

## Phase 5: Structured Output Format

Produce a **structured markdown answer** with these sections:

1. **Executive Summary** — What was researched, key findings with inline citations, high-level recommendation.
2. **Research Overview** — Sub-question count, query count, distinct sources, freshness/authority mix.
3. **Findings** — One subsection per sub-question: 2–4 paragraphs with \`[web:N]\` citations + optional "Key insights" bullets.
4. **Synthesis** — How pieces fit together; consensus vs trade-offs; important contradictions explained.
5. **Recommendations** — 2–4 prioritized actions with rationale and citations.
6. **References** *(optional)* — Appendix of \`web:N → title + URL\` for human inspection.

---

## Phase 6: Iterative Refinement

After the first pass, check for gaps:
- Sub-questions with 0–1 sources or only low-quality sources.
- Critical claims lacking a high-quality source.
- Missing recent or official sources on important topics.

If gaps exist: generate 1–5 targeted follow-up queries (add \`"limitations"\`, \`"criticism"\`, or \`after:2024\`), run another batched search, and integrate new evidence into existing sections.

Stop when additional searches stop changing conclusions, or after 2–3 passes — explicitly note remaining gaps in the answer.

---

## Implementation Notes

- Keep queries **short and focused** — concise queries perform better with most search tools.
- Prefer **fewer, better citations** — 1–3 strong sources per important claim is enough.
- If a fetch or analysis tool is available, extend this skill to retrieve raw pages, extract tables, or run quick computations before final synthesis.
`;

// Skills that should be pre-installed and active for all users.
// These are globally relevant to the platform (e.g. Cloudflare deployment).
const DEFAULT_SKILLS: InstalledSkill[] = [
  {
    name: 'wrangler',
    fullName: 'cloudflare/skills',
    description:
      'Cloudflare Wrangler — Workers, Pages, D1, R2, KV, and Cloudflare deployment workflows',
    url: 'https://github.com/cloudflare/skills',
    active: false,
    installedAt: '2025-01-01T00:00:00.000Z',
  },
  {
    name: 'auto-project-runner',
    fullName: 'tippyentertainment/skills',
    description:
      'Autonomous project runner — attaches to a project directory, loads memory, scans code, and works autonomously with minimal interruptions',
    url: 'https://github.com/tippyentertainment/skills',
    active: false,
    installedAt: '2025-01-01T00:00:00.000Z',
  },
  {
    name: 'autonomous-multi-ai-agents',
    fullName: 'tasking-tech/skills',
    description:
      'Coordinate multiple AI agents that can chat, use voice, and edit code/files/folders simultaneously while avoiding destructive collisions',
    url: 'https://tasking.tech',
    active: false,
    installedAt: '2025-01-01T00:00:00.000Z',
    content: AUTONOMOUS_MULTI_AI_AGENTS_SKILL,
  },
  {
    name: 'hosted-multi-agent-orchestrator',
    fullName: 'tasking-tech/skills',
    description:
      'Coordinate host + worker agents that listen/speak/code in parallel, switching between /api/chat and /api/generate with the lead always in control',
    url: 'https://tasking.tech',
    active: false,
    installedAt: '2025-01-01T00:00:00.000Z',
    content: HOSTED_MULTI_AGENT_ORCHESTRATOR_SKILL,
  },
  {
    name: 'worldclass-tailwind-v4-visual-design',
    fullName: 'tippyentertainment/skills',
    description:
      'World-class visual/UI design with Tailwind v4 tokens, cinematic layouts, Gemini image generation, and award-winning art direction for all frameworks',
    url: 'https://github.com/tippyentertainment/skills',
    active: false,
    installedAt: '2025-01-01T00:00:00.000Z',
    content: WORLDCLASS_TAILWIND_V4_VISUAL_DESIGN_SKILL,
  },
  {
    name: 'ui-ux-designer',
    fullName: 'tippyentertainment/skills',
    description:
      'End-to-end UI/UX workflow from discovery to production-ready UI, with strict phased design decisions',
    url: 'https://github.com/tippyentertainment/skills',
    active: false,
    installedAt: '2025-01-01T00:00:00.000Z',
    content: UI_UX_DESIGNER_SKILL,
  },
  {
    name: 'video-producer',
    fullName: 'notedit/happy-skills',
    description:
      'End-to-end Remotion video creation — orchestrates narrative structure, scene animation, visual styling, and rendering from natural language briefs',
    url: 'https://github.com/notedit/happy-skills',
    active: false,
    installedAt: '2026-02-18T00:00:00.000Z',
    skillMdUrl: undefined,
  },
  {
    name: 'music-video-producer',
    fullName: 'mmcmedia/openclaw-agents',
    description:
      'Music video production agent — coordinates visual direction, scene composition, and motion design for music-driven video projects',
    url: 'https://github.com/mmcmedia/openclaw-agents',
    active: false,
    installedAt: '2026-02-18T00:00:00.000Z',
  },
  {
    name: 'nano-banana-pro-prompts-recommend-skill',
    fullName: 'youmind-openlab/nano-banana-pro-prompts-recommend-skill',
    description:
      'AI prompt recommendation skill — suggests optimized prompts for visual content categories including social media, thumbnails, product marketing, game assets, and more',
    url: 'https://github.com/youmind-openlab/nano-banana-pro-prompts-recommend-skill',
    skillMdUrl:
      'https://raw.githubusercontent.com/youmind-openlab/nano-banana-pro-prompts-recommend-skill/main/SKILL.md',
    active: false,
    installedAt: '2026-02-18T00:00:00.000Z',
  },
  {
    name: 'ffmpeg-video-editor',
    fullName: 'sundial-org/awesome-openclaw-skills',
    description:
      'FFmpeg video editor — translates natural language into FFmpeg commands for trimming, converting, compressing, resizing, rotating, extracting audio, adding watermarks, burning subtitles, and merging clips',
    url: 'https://github.com/sundial-org/awesome-openclaw-skills',
    active: false,
    installedAt: '2026-02-18T00:00:00.000Z',
  },
  {
    name: 'websearch-deep',
    fullName: 'tasking-tech/skills',
    description:
      'Deep web research — problem decomposition, multi-query parallel search, source credibility ranking, inline citations, and iterative refinement for architecture decisions, comparisons, and multi-domain synthesis',
    url: 'https://tasking.tech',
    active: false,
    installedAt: '2026-02-18T00:00:00.000Z',
    content: WEBSEARCH_DEEP_SKILL,
  },
];

/** Load installed skills from localStorage, merging in any missing defaults */
export function loadInstalledSkills(): InstalledSkill[] {
  let skills: InstalledSkill[] = [];
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) skills = JSON.parse(stored);
  } catch (e) {
    console.error('Failed to load installed skills:', e);
  }

  // Merge in default skills that aren't already installed
  let changed = false;
  for (const def of DEFAULT_SKILLS) {
    const existing = skills.find((s) => s.name === def.name);
    if (!existing) {
      skills.push({ ...def });
      changed = true;
      continue;
    }

    if (def.skillMdUrl && existing.skillMdUrl !== def.skillMdUrl) {
      existing.skillMdUrl = def.skillMdUrl;
      changed = true;
    }
  }
  if (changed) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(skills));
    } catch {
      /* best-effort */
    }
    // Fetch SKILL.md content for newly-added defaults in the background
    for (const def of DEFAULT_SKILLS) {
      const skill = skills.find((s) => s.name === def.name && !s.content && s.skillMdUrl);
      if (skill?.skillMdUrl) {
        fetchDefaultSkillContent(skill);
      }
    }
  }
  return skills;
}

/** Background-fetch SKILL.md for a default skill and persist it */
function fetchDefaultSkillContent(skill: InstalledSkill) {
  if (!skill.skillMdUrl) return;

  const candidates = [skill.skillMdUrl];

  const tryNext = (index: number) => {
    if (index >= candidates.length) return;
    fetch(candidates[index])
      .then((res) => (res.ok ? res.text() : null))
      .then((content) => {
        if (!content) {
          tryNext(index + 1);
          return;
        }
        try {
          const stored = localStorage.getItem(STORAGE_KEY);
          if (!stored) return;
          const skills: InstalledSkill[] = JSON.parse(stored);
          const idx = skills.findIndex((s) => s.name === skill.name);
          if (idx !== -1 && !skills[idx].content) {
            skills[idx].content = content;
            localStorage.setItem(STORAGE_KEY, JSON.stringify(skills));
          }
        } catch {
          /* best-effort */
        }
      })
      .catch(() => {
        tryNext(index + 1);
      });
  };
  tryNext(0);
}

type SkillTab = 'installed' | 'search';

export const SkillSearchModal: React.FC<SkillSearchModalProps> = ({
  open,
  onClose,
  onInstallSkill,
  initialQuery,
  onActiveSkillsChange,
  searchEndpoint = '/_api/skills/search',
}) => {
  const SEARCH_PAGE_SIZE = 10;
  const isTaskingBotSkillsMode = searchEndpoint.includes('/_api/taskingbot/skills-search');
  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  const [activeTab, setActiveTab] = useState<SkillTab>(initialQuery ? 'search' : 'installed');
  const [search, setSearch] = useState(initialQuery || '');
  const [results, setResults] = useState<SkillResult[]>([]);
  const [mpResults, setMpResults] = useState<SkillResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [mpLoading, setMpLoading] = useState(false);
  const [githubPage, setGithubPage] = useState(1);
  const [skillsMpPage, setSkillsMpPage] = useState(1);
  const [githubHasNext, setGithubHasNext] = useState(false);
  const [skillsMpHasNext, setSkillsMpHasNext] = useState(false);
  const [installing, setInstalling] = useState<string | null>(null);
  const [installedSkills, setInstalledSkills] = useState<InstalledSkill[]>(() =>
    loadInstalledSkills(),
  );
  // Multi-select state for batch operations
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);

  const buildInstallCommand = (skill: SkillResult) => {
    if (!isTaskingBotSkillsMode) {
      return `npx skills add ${skill.url || skill.fullName} --skill ${skill.name} -g -y`;
    }

    const sourceHint =
      skill.source === 'clawdhub'
        ? 'clawdhub'
        : skill.source === 'github-openclaw' || skill.source === 'openclaw'
          ? 'openclaw'
          : undefined;

    const parts = [`Install and enable the TaskingBot skill "${skill.name}" using install_skill.`];
    if (sourceHint) parts.push(`Source: ${sourceHint}.`);
    if (skill.skillMdUrl) parts.push(`SKILL.md URL: ${skill.skillMdUrl}`);
    if (skill.url) parts.push(`Repo/URL: ${skill.url}`);
    return parts.join(' ');
  };

  const toggleSelectSkill = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (activeTab === 'installed') {
      const allKeys = new Set(installedSkills.map((s) => buildSkillKey(s)));
      setSelectedKeys(allKeys);
    }
  };

  const handleSelectNone = () => setSelectedKeys(new Set());

  const handleBatchActivate = () => {
    const updated = installedSkills.map((s) =>
      selectedKeys.has(buildSkillKey(s)) ? { ...s, active: true } : s,
    );
    setInstalledSkills(updated);
    saveInstalledSkills(updated);
    onActiveSkillsChange?.(updated.filter((s) => s.active));
    toast.success(`${selectedKeys.size} skill(s) activated`);
    setSelectedKeys(new Set());
  };

  const handleBatchDeactivate = () => {
    const updated = installedSkills.map((s) =>
      selectedKeys.has(buildSkillKey(s)) ? { ...s, active: false } : s,
    );
    setInstalledSkills(updated);
    saveInstalledSkills(updated);
    onActiveSkillsChange?.(updated.filter((s) => s.active));
    toast.success(`${selectedKeys.size} skill(s) deactivated`);
    setSelectedKeys(new Set());
  };

  const handleBatchRemove = () => {
    const updated = installedSkills.filter((s) => !selectedKeys.has(buildSkillKey(s)));
    setInstalledSkills(updated);
    saveInstalledSkills(updated);
    onActiveSkillsChange?.(updated.filter((s) => s.active));
    toast.info(`${selectedKeys.size} skill(s) removed`);
    setSelectedKeys(new Set());
  };

  // Multi-select for search results (batch install)
  const [selectedSearchKeys, setSelectedSearchKeys] = useState<Set<string>>(new Set());

  const toggleSelectSearchSkill = (key: string) => {
    setSelectedSearchKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const installedKeySet = useMemo(
    () => new Set(installedSkills.map((s) => buildSkillKey(s))),
    [installedSkills],
  );
  const rankedGithubResults = useMemo(
    () => rankSkillResults(results, search, installedKeySet),
    [results, search, installedKeySet],
  );
  const rankedSkillsMpResults = useMemo(
    () => rankSkillResults(mpResults, search, installedKeySet),
    [mpResults, search, installedKeySet],
  );

  // Update search when initialQuery changes
  useEffect(() => {
    if (initialQuery) setSearch(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    setGithubPage(1);
    setSkillsMpPage(1);
  }, [search]);

  // Notify parent when active skills change
  useEffect(() => {
    if (onActiveSkillsChange) {
      onActiveSkillsChange(installedSkills.filter((s) => s.active));
    }
  }, [installedSkills, onActiveSkillsChange]);

  // Search GitHub for agent skill repos
  useEffect(() => {
    if (isTaskingBotSkillsMode) {
      setResults([]);
      setGithubHasNext(false);
      return;
    }

    if (!search.trim()) {
      setResults([]);
      setGithubHasNext(false);
      return;
    }

    const searchSkills = async () => {
      setLoading(true);
      try {
        // First try a focused query for skill repos, then broaden if results are sparse.
        const queries = [`${search} skill SKILL.md in:path`, `${search} agent skill`, `${search}`];

        let allItems: any[] = [];

        for (const q of queries) {
          const query = encodeURIComponent(q);
          const response = await fetch(
            `https://api.github.com/search/repositories?q=${query}&sort=stars&order=desc&per_page=${SEARCH_PAGE_SIZE}&page=${githubPage}`,
          );
          if (!response.ok) {
            if (response.status === 403) {
              toast.error('GitHub API rate limit reached. Try again in a minute.');
            }
            continue;
          }
          const data = await response.json();
          const items = data.items || [];
          if (items.length > 0) {
            allItems = items;
            break; // Use the first query that returns results
          }
        }

        // Deduplicate by full_name
        const seen = new Set<string>();
        const repos: SkillResult[] = [];
        for (const repo of allItems) {
          const key = repo.full_name;
          if (seen.has(key)) continue;
          seen.add(key);
          repos.push({
            name: repo.name,
            fullName: repo.full_name,
            description: repo.description || 'No description',
            stars: repo.stargazers_count || 0,
            url: repo.html_url,
          });
        }
        setResults(repos);
        setGithubHasNext(repos.length >= SEARCH_PAGE_SIZE);
      } catch (err) {
        console.error('Failed to search skills:', err);
        setResults([]);
        setGithubHasNext(false);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(searchSkills, 400);
    return () => clearTimeout(timeoutId);
  }, [search, githubPage, isTaskingBotSkillsMode]);

  // Search SkillsMP marketplace (or TaskingBot ClawdHub/OpenClaw endpoint)
  useEffect(() => {
    const shouldBrowseWithoutQuery = isTaskingBotSkillsMode;
    if (!search.trim() && !shouldBrowseWithoutQuery) {
      setMpResults([]);
      setSkillsMpHasNext(false);
      return;
    }

    const searchSkillsMP = async () => {
      setMpLoading(true);
      try {
        const q = encodeURIComponent(search);
        const offset = (skillsMpPage - 1) * SEARCH_PAGE_SIZE;
        // Use AI semantic search for natural-language queries (longer or question-like)
        const useAiSearch =
          search.trim().split(/\s+/).length >= 3 ||
          /\?|how\s|what\s|best\s|create\s|build\s|get\s/i.test(search.trim());
        const mode = useAiSearch ? 'ai' : 'keyword';
        const sortBy = 'stars';
        // Build URL based on endpoint type
        let url: string;
        if (isTaskingBotSkillsMode) {
          // TaskingBot endpoint (ClawdHub): simpler params
          url = search.trim()
            ? `${searchEndpoint}?q=${q}&limit=${SEARCH_PAGE_SIZE}`
            : `${searchEndpoint}?limit=${SEARCH_PAGE_SIZE}`;
        } else {
          // SkillsMP endpoint: full params
          url = `${searchEndpoint}?q=${q}&limit=${SEARCH_PAGE_SIZE}&page=${skillsMpPage}&offset=${offset}&sortBy=${sortBy}&mode=${mode}`;
        }
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Skill search failed: ${res.status}`);
        const data = await res.json();
        const arr = Array.isArray(data.skills)
          ? data.skills
          : Array.isArray(data.results)
            ? data.results
            : Array.isArray(data.data)
              ? data.data
              : [];
        const skills: SkillResult[] = arr.map((s: any) => {
          const name = s.name || s.title || s.slug || 'Untitled';
          const source = isTaskingBotSkillsMode
            ? s.source || (s.githubUrl ? 'openclaw' : 'clawdhub')
            : 'skillsmp';
          return {
            name,
            fullName: isTaskingBotSkillsMode
              ? s.full_name || (s.author ? `${s.author}/${name}` : `${String(source)}/${name}`)
              : s.full_name || (s.author ? `${s.author}/${name}` : name),
            description: s.description || s.summary || 'No description',
            stars: s.stars || s.stargazers_count || 0,
            skillMdUrl: s.skillMdUrl || s.skill_md_url || s.raw_url || undefined,
            url:
              s.url ||
              s.githubUrl ||
              s.github_url ||
              s.html_url ||
              (!isTaskingBotSkillsMode ? `https://skillsmp.com/skills/${s.slug || name}` : ''),
            source,
          };
        });
        setMpResults(skills);
        const hasNext = isTaskingBotSkillsMode
          ? false
          : Boolean(data?.hasNext) ||
            Boolean(data?.has_more) ||
            Boolean(data?.pagination?.hasNext) ||
            Boolean(data?.pagination?.has_more) ||
            skills.length >= SEARCH_PAGE_SIZE;
        setSkillsMpHasNext(hasNext);

        // Surface missing credentials hint (e.g. GitHub PAT for better search)
        if (Array.isArray(data?.missingCredentials) && data.missingCredentials.length > 0) {
          const missing = data.missingCredentials.join(', ');
          toast.info(
            `Add ${missing} credentials in Settings → Integrations for better search results`,
            { duration: 6000 },
          );
        }
      } catch (err) {
        console.error(
          isTaskingBotSkillsMode ? 'TaskingBot skill search failed:' : 'SkillsMP search failed:',
          err,
        );
        setMpResults([]);
        setSkillsMpHasNext(false);
      } finally {
        setMpLoading(false);
      }
    };

    const timeoutId = setTimeout(searchSkillsMP, 400);
    return () => clearTimeout(timeoutId);
  }, [search, skillsMpPage, searchEndpoint, isTaskingBotSkillsMode]);

  /** Fetch SKILL.md content from a GitHub repo */
  const fetchSkillContent = async (
    fullName: string,
    skillName?: string,
    explicitSkillMdUrl?: string,
  ): Promise<string | undefined> => {
    if (explicitSkillMdUrl) {
      try {
        const res = await fetch(explicitSkillMdUrl);
        if (res.ok) {
          const text = await res.text();
          if (text.trim()) return text.trim();
        }
      } catch {
        // Fall through to path probing.
      }
    }

    const branches = ['main', 'master'];

    // Build path candidates — try skill subdirectory first (for multi-skill repos),
    // then root-level paths
    const rootPaths = ['SKILL.md', 'skill.md', 'SKILLS.md'];
    const allPaths: string[] = [];

    if (skillName) {
      // Multi-skill repo pattern: skills/<name>/SKILL.md
      for (const file of rootPaths) {
        allPaths.push(`skills/${skillName}/${file}`);
      }
    }
    allPaths.push(...rootPaths);

    for (const path of allPaths) {
      for (const branch of branches) {
        try {
          const res = await fetch(
            `https://raw.githubusercontent.com/${fullName}/${branch}/${path}`,
          );
          if (res.ok) {
            const text = await res.text();
            if (text.trim()) return text.trim();
          }
        } catch {
          // Try next
        }
      }
    }

    // Fallback: try README.md
    for (const branch of branches) {
      try {
        const res = await fetch(
          `https://raw.githubusercontent.com/${fullName}/${branch}/README.md`,
        );
        if (res.ok) {
          const text = await res.text();
          if (text.trim()) return text.trim();
        }
      } catch {
        // No content available
      }
    }
    return undefined;
  };

  const addToInstalled = async (skill: SkillResult) => {
    const alreadyInstalled = installedSkills.some(
      (s) => s.fullName === skill.fullName && s.name === skill.name,
    );
    if (alreadyInstalled) return;

    // Fetch the actual skill instructions (pass skill name for subdirectory lookups)
    let content: string | undefined;
    try {
      content = await fetchSkillContent(skill.fullName, skill.name, skill.skillMdUrl);
      if (content) {
        console.log(
          `[Skills] Fetched SKILL.md for ${skill.fullName}/${skill.name} (${content.length} chars)`,
        );
      } else {
        console.warn(
          `[Skills] No SKILL.md found for ${skill.fullName}/${skill.name}, using description only`,
        );
      }
    } catch (err) {
      console.error(
        `[Skills] Failed to fetch skill content for ${skill.fullName}/${skill.name}:`,
        err,
      );
    }

    const newSkill: InstalledSkill = {
      name: skill.name,
      fullName: skill.fullName,
      description: skill.description,
      url: skill.url,
      skillMdUrl: skill.skillMdUrl,
      active: true,
      installedAt: new Date().toISOString(),
      content,
    };
    const updated = [...installedSkills, newSkill];
    setInstalledSkills(updated);
    saveInstalledSkills(updated);
    onActiveSkillsChange?.(updated.filter((s) => s.active));
  };

  const skillKey = (s: { fullName: string; name: string }) => buildSkillKey(s);

  const handleInstall = async (skill: SkillResult) => {
    setInstalling(skillKey(skill));

    // Security scan: check SKILL.md content before installation
    try {
      const content = await fetchSkillContent(skill.fullName, skill.name, skill.skillMdUrl);
      if (content && content.length > 20) {
        const scan = await scanSkillBeforeInstall(skill.name, content);
        if (!scan.safe) {
          const issueList = scan.issues.map((i) => `${i.code}: ${i.message}`).join('\n');
          toast.error(`Skill "${skill.name}" blocked by security scan`, { duration: 6000 });
          console.warn(`[Skills] BLOCKED "${skill.name}":`, issueList);
          setInstalling(null);
          return;
        }
        if (scan.issues.length > 0) {
          toast.warning(
            `Skill "${skill.name}" has ${scan.issues.length} warning(s) — installed with caution`,
            { duration: 4000 },
          );
        }
      }
    } catch (scanErr) {
      console.warn('[Skills] Security scan failed, proceeding:', scanErr);
    }

    const command = buildInstallCommand(skill);
    onInstallSkill(command);
    await addToInstalled(skill);
    toast.success(`Skill "${skill.name}" installed and activated`);
    setTimeout(() => setInstalling(null), 2000);
  };

  const handleToggleActive = (fullName: string, name: string) => {
    const updated = installedSkills.map((s) =>
      s.fullName === fullName && s.name === name ? { ...s, active: !s.active } : s,
    );
    setInstalledSkills(updated);
    saveInstalledSkills(updated);
    onActiveSkillsChange?.(updated.filter((s) => s.active));
  };

  const handleRemoveSkill = (fullName: string, name: string) => {
    const updated = installedSkills.filter((s) => !(s.fullName === fullName && s.name === name));
    setInstalledSkills(updated);
    saveInstalledSkills(updated);
    onActiveSkillsChange?.(updated.filter((s) => s.active));
    toast.info('Skill removed');
  };

  const isInstalled = (fullName: string, name: string) =>
    installedSkills.some((s) => s.fullName === fullName && s.name === name);

  const handleBatchInstall = async () => {
    const allSearchSkills = [
      ...(isTaskingBotSkillsMode ? TASKINGBOT_CURATED_SOURCES : KNOWN_SOURCES),
      ...rankedGithubResults,
      ...rankedSkillsMpResults,
    ];
    const toInstall = allSearchSkills.filter(
      (s) => selectedSearchKeys.has(buildSkillKey(s)) && !isInstalled(s.fullName, s.name),
    );
    for (const skill of toInstall) {
      await addToInstalled(skill);
      const command = buildInstallCommand(skill);
      onInstallSkill(command);
    }
    toast.success(`${toInstall.length} skill(s) installed and activated`);
    setSelectedSearchKeys(new Set());
  };

  if (!open) return null;

  const sourceBadgeMeta = (source?: SkillResult['source']) => {
    switch (source) {
      case 'clawdhub':
        return { label: 'ClawdHub', background: 'linear-gradient(135deg, #f97316, #ef4444)' };
      case 'github-openclaw':
      case 'openclaw':
        return { label: 'OpenClaw', background: 'linear-gradient(135deg, #22c55e, #0ea5e9)' };
      case 'local':
        return { label: 'Local', background: 'linear-gradient(135deg, #64748b, #475569)' };
      case 'skillsmp':
      default:
        return { label: 'SkillsMP', background: 'linear-gradient(135deg, #8b5cf6, #6366f1)' };
    }
  };

  const modalContent = (
    <div className={styles.backdrop} role="dialog" aria-modal="true" onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.titleContainer}>
            <span style={{ fontSize: 22 }}>&#x1F50D;</span>
            <h2 className={styles.title}>Agent Skills</h2>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                window.open(isTaskingBotSkillsMode ? 'https://github.com/openclaw/skills' : 'https://skills.sh/', '_blank')
              }
            >
              {isTaskingBotSkillsMode ? 'OpenClaw' : 'Skills.sh'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                window.open(isTaskingBotSkillsMode ? 'https://clawdhub.ai/' : 'https://skillsmp.com/', '_blank')
              }
            >
              {isTaskingBotSkillsMode ? 'ClawdHub' : 'SkillsMP'}
            </Button>
            <Button variant="outline" onClick={onClose} className={styles.closeButton}>
              Close
            </Button>
          </div>
        </div>
        <div className={styles.body}>
          {/* Tab pills */}
          <div className={styles.tabPills}>
            <button
              className={`${styles.tabPill} ${activeTab === 'installed' ? styles.tabPillActive : ''}`}
              onClick={() => setActiveTab('installed')}
            >
              Installed ({installedSkills.filter((s) => s.active).length}/{installedSkills.length})
            </button>
            <button
              className={`${styles.tabPill} ${activeTab === 'search' ? styles.tabPillActive : ''}`}
              onClick={() => setActiveTab('search')}
            >
              Search
            </button>
          </div>

          {/* ── Installed Tab ── */}
          {activeTab === 'installed' && (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                  padding: '0 2px',
                  gap: 8,
                }}
              >
                <p
                  style={{
                    fontSize: 12,
                    color: 'var(--muted-foreground)',
                    margin: 0,
                  }}
                >
                  Toggle skills on/off to include them in AI agent context. Active skills are
                  injected into every AI conversation.
                </p>
                {installedSkills.length > 0 && (
                  <Button
                    size="sm"
                    variant={isMultiSelectMode ? 'primary' : 'outline'}
                    onClick={() => {
                      setIsMultiSelectMode((v) => !v);
                      if (isMultiSelectMode) setSelectedKeys(new Set());
                    }}
                    style={{ flexShrink: 0, fontSize: 11 }}
                  >
                    {isMultiSelectMode ? 'Done' : 'Select'}
                  </Button>
                )}
              </div>

              {/* Batch actions bar */}
              {isMultiSelectMode && installedSkills.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 8px',
                    marginBottom: 8,
                    background: 'var(--muted)',
                    borderRadius: 6,
                    fontSize: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ color: 'var(--muted-foreground)', fontWeight: 500 }}>
                    {selectedKeys.size} selected
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleSelectAll}
                    style={{ fontSize: 11 }}
                  >
                    All
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleSelectNone}
                    style={{ fontSize: 11 }}
                  >
                    None
                  </Button>
                  <span style={{ flex: 1 }} />
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleBatchActivate}
                    disabled={selectedKeys.size === 0}
                    style={{ fontSize: 11 }}
                  >
                    Activate
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleBatchDeactivate}
                    disabled={selectedKeys.size === 0}
                    style={{ fontSize: 11 }}
                  >
                    Deactivate
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleBatchRemove}
                    disabled={selectedKeys.size === 0}
                    style={{ fontSize: 11, color: 'var(--danger, #ef4444)' }}
                  >
                    Remove
                  </Button>
                </div>
              )}

              {installedSkills.length === 0 ? (
                <div className={styles.empty} style={{ padding: 24 }}>
                  No skills installed yet. Switch to the <strong>Search</strong> tab to find and
                  install skills.
                </div>
              ) : (
                installedSkills.map((skill) => {
                  const key = buildSkillKey(skill);
                  return (
                    <div key={key} className={styles.item}>
                      {isMultiSelectMode && (
                        <input
                          type="checkbox"
                          checked={selectedKeys.has(key)}
                          onChange={() => toggleSelectSkill(key)}
                          style={{
                            accentColor: 'var(--ember, #c8953f)',
                            width: 16,
                            height: 16,
                            flexShrink: 0,
                            cursor: 'pointer',
                            marginRight: 4,
                          }}
                        />
                      )}
                      <label className={styles.skillToggle}>
                        <input
                          type="checkbox"
                          checked={skill.active}
                          onChange={() => handleToggleActive(skill.fullName, skill.name)}
                          className={styles.skillCheckbox}
                        />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              flexWrap: 'wrap',
                            }}
                          >
                            <span className={styles.pkgName} style={{ fontWeight: 600 }}>
                              {skill.name}
                            </span>
                            {skill.active && <span className={styles.activeBadge}>Active</span>}
                            {skill.content && (
                              <span
                                style={{
                                  fontSize: 10,
                                  padding: '1px 5px',
                                  borderRadius: 4,
                                  background: 'color-mix(in srgb, var(--primary) 12%, transparent)',
                                  color: 'var(--primary)',
                                  fontWeight: 600,
                                }}
                              >
                                Built-in
                              </span>
                            )}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              color: 'var(--muted-foreground)',
                              marginTop: 2,
                            }}
                          >
                            {skill.fullName}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: 'var(--foreground)',
                              opacity: 0.8,
                              marginTop: 3,
                              lineHeight: 1.4,
                            }}
                          >
                            {skill.description}
                          </div>
                        </div>
                      </label>
                      <div className={styles.itemActions}>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRemoveSkill(skill.fullName, skill.name)}
                          style={{ color: 'var(--danger, #ef4444)', fontSize: 12 }}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  );
                })
              )}
            </>
          )}

          {/* ── Search Tab ── */}
          {activeTab === 'search' && (
            <>
              <input
                id="skill-search"
                name="skill-search"
                type="text"
                placeholder="Search agent skills (e.g., react, testing, deploy)..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={styles.searchInput}
                autoFocus
              />

              {/* Batch install bar for search results */}
              {selectedSearchKeys.size > 0 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 10px',
                    marginBottom: 8,
                    background: 'var(--muted)',
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                >
                  <span style={{ color: 'var(--muted-foreground)', fontWeight: 500 }}>
                    {selectedSearchKeys.size} selected
                  </span>
                  <span style={{ flex: 1 }} />
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleBatchInstall}
                    style={{ fontSize: 11 }}
                  >
                    Install Selected
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedSearchKeys(new Set())}
                    style={{ fontSize: 11 }}
                  >
                    Clear
                  </Button>
                </div>
              )}

              {(loading || mpLoading) && (
                <div className={styles.loading}>
                  Searching{loading ? ' GitHub' : ''}
                  {loading && mpLoading ? ' &' : ''}
                  {mpLoading ? (isTaskingBotSkillsMode ? ' ClawdHub + OpenClaw' : ' SkillsMP') : ''}
                  ...
                </div>
              )}

              {!isTaskingBotSkillsMode && (
                <section className={styles.section}>
                  <h4 className={styles.sectionTitle}>Known Sources</h4>
                  {KNOWN_SOURCES.map((skill) => {
                    const key = skillKey(skill);
                    const installed = isInstalled(skill.fullName, skill.name);
                    return (
                      <div key={key} className={styles.item}>
                        {!installed && (
                          <input
                            type="checkbox"
                            checked={selectedSearchKeys.has(key)}
                            onChange={() => toggleSelectSearchSkill(key)}
                            style={{
                              accentColor: 'var(--ember, #c8953f)',
                              width: 15,
                              height: 15,
                              flexShrink: 0,
                              cursor: 'pointer',
                              marginRight: 4,
                            }}
                          />
                        )}
                        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                          <span className={styles.pkgName} style={{ fontWeight: 600 }}>
                            {skill.name}
                          </span>
                          {installed && <span className={styles.installedBadge}>Installed</span>}
                          <div style={{ fontSize: 13, opacity: 0.7, marginTop: 2 }}>
                            {skill.fullName} — {skill.description}
                          </div>
                        </div>
                        <div className={styles.itemActions}>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleInstall(skill)}
                            disabled={installing === key || installed}
                          >
                            {installed
                              ? 'Installed'
                              : installing === key
                                ? 'Installing...'
                                : 'Install'}
                          </Button>
                          <Button
                            size="sm"
                            variant="link"
                            onClick={() => window.open(skill.url, '_blank')}
                          >
                            View
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </section>
              )}

              {isTaskingBotSkillsMode && (
                <section className={styles.section}>
                  <h4 className={styles.sectionTitle}>Curated TaskingBot Skills</h4>
                  {TASKINGBOT_CURATED_SOURCES.map((skill) => {
                    const key = skillKey(skill);
                    const installed = isInstalled(skill.fullName, skill.name);
                    return (
                      <div key={key} className={styles.item}>
                        {!installed && (
                          <input
                            type="checkbox"
                            checked={selectedSearchKeys.has(key)}
                            onChange={() => toggleSelectSearchSkill(key)}
                            style={{
                              accentColor: 'var(--ember, #c8953f)',
                              width: 15,
                              height: 15,
                              flexShrink: 0,
                              cursor: 'pointer',
                              marginRight: 4,
                            }}
                          />
                        )}
                        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                          <span className={styles.pkgName} style={{ fontWeight: 600 }}>
                            {skill.name}
                          </span>
                          {installed && <span className={styles.installedBadge}>Installed</span>}
                          <div style={{ fontSize: 13, opacity: 0.7, marginTop: 2 }}>
                            {skill.fullName}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              opacity: 0.6,
                              marginTop: 2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {skill.description}
                          </div>
                        </div>
                        <div className={styles.itemActions}>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleInstall(skill)}
                            disabled={installing === key || installed}
                          >
                            {installed
                              ? 'Installed'
                              : installing === key
                                ? 'Installing...'
                                : 'Install'}
                          </Button>
                          <Button
                            size="sm"
                            variant="link"
                            onClick={() => window.open(skill.url, '_blank')}
                          >
                            View
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </section>
              )}

              {!isTaskingBotSkillsMode &&
                (rankedGithubResults.length > 0 || (search.trim() && !loading)) && (
                  <section className={styles.section}>
                    <div className={styles.sectionHeader}>
                      <h4 className={styles.sectionTitle}>Search Results</h4>
                      <div className={styles.paginationControls}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setGithubPage((p) => Math.max(1, p - 1))}
                          disabled={loading || githubPage <= 1}
                        >
                          Prev
                        </Button>
                        <span className={styles.paginationLabel}>Page {githubPage}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setGithubPage((p) => p + 1)}
                          disabled={loading || !githubHasNext}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                    {rankedGithubResults.map((skill) => {
                      const key = skillKey(skill);
                      const installed = isInstalled(skill.fullName, skill.name);
                      return (
                        <div key={key} className={styles.item}>
                          {!installed && (
                            <input
                              type="checkbox"
                              checked={selectedSearchKeys.has(key)}
                              onChange={() => toggleSelectSearchSkill(key)}
                              style={{
                                accentColor: 'var(--ember, #c8953f)',
                                width: 15,
                                height: 15,
                                flexShrink: 0,
                                cursor: 'pointer',
                                marginRight: 4,
                              }}
                            />
                          )}
                          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                            <span className={styles.pkgName} style={{ fontWeight: 600 }}>
                              {skill.name}
                            </span>
                            {installed && <span className={styles.installedBadge}>Installed</span>}
                            <div style={{ fontSize: 13, opacity: 0.7, marginTop: 2 }}>
                              {skill.fullName}
                              {skill.stars > 0 && ` — ${skill.stars.toLocaleString()} stars`}
                            </div>
                            <div
                              style={{
                                fontSize: 12,
                                opacity: 0.6,
                                marginTop: 2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {skill.description}
                            </div>
                          </div>
                          <div className={styles.itemActions}>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleInstall(skill)}
                              disabled={installing === key || installed}
                            >
                              {installed
                                ? 'Installed'
                                : installing === key
                                  ? 'Installing...'
                                  : 'Install'}
                            </Button>
                            <Button
                              size="sm"
                              variant="link"
                              onClick={() => window.open(skill.url, '_blank')}
                            >
                              View
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </section>
                )}

              {(rankedSkillsMpResults.length > 0 ||
                ((search.trim() || isTaskingBotSkillsMode) && !mpLoading)) && (
                <section className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <h4 className={styles.sectionTitle}>
                      {isTaskingBotSkillsMode ? 'OpenClaw + ClawdHub Skills' : 'SkillsMP Marketplace'}
                      <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.5, marginLeft: 8 }}>
                        {isTaskingBotSkillsMode
                          ? 'community skills for TaskingBot tooling'
                          : 'powered by skillsmp.com'}
                      </span>
                    </h4>
                    <div className={styles.paginationControls}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSkillsMpPage((p) => Math.max(1, p - 1))}
                        disabled={mpLoading || skillsMpPage <= 1}
                      >
                        Prev
                      </Button>
                      <span className={styles.paginationLabel}>Page {skillsMpPage}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSkillsMpPage((p) => p + 1)}
                        disabled={mpLoading || !skillsMpHasNext}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                  {rankedSkillsMpResults.map((skill) => {
                    const key = skillKey(skill);
                    const installed = isInstalled(skill.fullName, skill.name);
                    return (
                      <div key={`mp-${skill.fullName}`} className={styles.item}>
                        {!installed && (
                          <input
                            type="checkbox"
                            checked={selectedSearchKeys.has(key)}
                            onChange={() => toggleSelectSearchSkill(key)}
                            style={{
                              accentColor: 'var(--ember, #c8953f)',
                              width: 15,
                              height: 15,
                              flexShrink: 0,
                              cursor: 'pointer',
                              marginRight: 4,
                            }}
                          />
                        )}
                        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                          <span className={styles.pkgName} style={{ fontWeight: 600 }}>
                            {skill.name}
                          </span>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              background:
                                sourceBadgeMeta(skill.source).background ||
                                'linear-gradient(135deg, #8b5cf6, #6366f1)',
                              color: 'white',
                              padding: '1px 6px',
                              borderRadius: 4,
                              marginLeft: 6,
                              verticalAlign: 'middle',
                            }}
                          >
                            {sourceBadgeMeta(skill.source).label}
                          </span>
                          {installed && <span className={styles.installedBadge}>Installed</span>}
                          <div style={{ fontSize: 13, opacity: 0.7, marginTop: 2 }}>
                            {skill.fullName}
                            {skill.stars > 0 && ` — ${skill.stars.toLocaleString()} stars`}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              opacity: 0.6,
                              marginTop: 2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {skill.description}
                          </div>
                        </div>
                        <div className={styles.itemActions}>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleInstall(skill)}
                            disabled={installing === key || installed}
                          >
                            {installed
                              ? 'Installed'
                              : installing === key
                                ? 'Installing...'
                                : 'Install'}
                          </Button>
                          <Button
                            size="sm"
                            variant="link"
                            onClick={() => window.open(skill.url, '_blank')}
                          >
                            View
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </section>
              )}

              {(search.trim() || isTaskingBotSkillsMode) &&
                !loading &&
                !mpLoading &&
                rankedGithubResults.length === 0 &&
                rankedSkillsMpResults.length === 0 && (
                  <section className={styles.section}>
                    <div className={styles.empty}>
                      {isTaskingBotSkillsMode
                        ? 'No skills found. Try a different keyword or browse '
                        : 'No results found. Try different keywords or browse '}
                      <a
                        href={isTaskingBotSkillsMode ? 'https://github.com/openclaw/skills' : 'https://skills.sh/'}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'var(--primary, #3b82f6)' }}
                      >
                        {isTaskingBotSkillsMode ? 'OpenClaw' : 'skills.sh'}
                      </a>
                      {isTaskingBotSkillsMode && (
                        <>
                          {' '}or{' '}
                          <a
                            href="https://clawdhub.ai/"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--primary, #3b82f6)' }}
                          >
                            ClawdHub
                          </a>
                        </>
                      )}
                      {!isTaskingBotSkillsMode && (
                        <>
                          {' '}for the full ecosystem.
                        </>
                      )}
                      {isTaskingBotSkillsMode && (
                        <>
                          {' '}for the full catalog.
                        </>
                      )}
                    </div>
                  </section>
                )}

              <section
                className={styles.section}
                style={{ marginTop: 12, opacity: 0.7, fontSize: 13 }}
              >
                <p>
                  Skills extend AI agent capabilities. Install from GitHub repos or browse{' '}
                  <a
                    href="https://skills.sh/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--primary, #3b82f6)' }}
                  >
                    skills.sh
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://skillsmp.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--primary, #3b82f6)' }}
                  >
                    skillsmp.com
                  </a>{' '}
                  for the full ecosystem.
                </p>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modalContent;
  return createPortal(modalContent, document.body);
};
